import { loadBurbankCapPublishedBundle } from '../../burbankCapPublished/loadBurbankCapPublished';
import { BURBANK_SOCAL_INDUSTRIAL_GAS_THERMS_BY_YEAR } from './burbankSoCalGasIndustrialObserved';
import type { Eia861CanonicalElectricityYear, Eia861SectorYearValue } from '../../eia861UtilityElectricity/types';
import type { CoverageStatus, SourceClass } from './types';

export const BURBANK_CAP_PUBLISHED_YEARS = [2010, 2019, 2022, 2024] as const;

export const BURBANK_FIRST_CAP_GAS_YEAR = 2010;
export const BURBANK_NONRES_ELEC_CAP_YEARS = [2019, 2022] as const;

export type BlendedPoint = {
  value: number;
  coverage: CoverageStatus;
  sourceClass: SourceClass;
  methodId: string;
  sourceNote: string;
};

function capResElec(year: number): number | null {
  const row = loadBurbankCapPublishedBundle().capElectricity[String(year)];
  return row?.residential_kwh ?? null;
}

function capComElec(year: number): number | null {
  const row = loadBurbankCapPublishedBundle().capElectricity[String(year)];
  return row?.commercial_kwh ?? null;
}

function capResGas(year: number): number | null {
  const row = loadBurbankCapPublishedBundle().capGas[String(year)];
  return row?.residential_therms ?? null;
}

function capNonresGas(year: number): number | null {
  const row = loadBurbankCapPublishedBundle().capGas[String(year)];
  return row?.nonresidential_therms ?? null;
}

function eiaCoverage(status: string): CoverageStatus {
  if (status === 'observed_eia861') return 'complete_observed';
  if (status === 'interpolated_between_eia_observations') return 'interpolated';
  return 'extrapolated';
}

function eiaSourceClass(status: string): SourceClass {
  return status === 'observed_eia861' ? 'utility_observed' : 'modeled';
}

function capPoint(value: number, methodId: string): BlendedPoint {
  return {
    value,
    coverage: 'cap_published',
    sourceClass: 'cap_published',
    methodId,
    sourceNote: 'Published CAP (Rincon 2024 memorandum Table 3)',
  };
}

function eiaPoint(sector: Eia861SectorYearValue, methodId: string, scope: string): BlendedPoint {
  return {
    value: sector.kwh,
    coverage: eiaCoverage(sector.status),
    sourceClass: eiaSourceClass(sector.status),
    methodId,
    sourceNote: `${scope}; EIA-861 BWP utility 2507; status ${sector.status}`,
  };
}

/** CAP anchors only; linear interpolation between; flat before first / after last. */
export function blendCapOnlyThermsSeries(
  year: number,
  anchors: Record<number, number>,
  methodId: string
): BlendedPoint {
  const years = Object.keys(anchors)
    .map(Number)
    .sort((a, b) => a - b);
  if (anchors[year] != null) {
    return capPoint(anchors[year], methodId);
  }
  const first = years[0];
  const last = years[years.length - 1];
  if (year < first) {
    return {
      value: anchors[first],
      coverage: 'extrapolated',
      sourceClass: 'cap_published',
      methodId: `${methodId}_flat_before`,
      sourceNote: 'Flat extension from first published CAP anchor',
    };
  }
  if (year > last) {
    return {
      value: anchors[last],
      coverage: 'extrapolated',
      sourceClass: 'cap_published',
      methodId: `${methodId}_flat_after`,
      sourceNote: 'Flat extension from last published CAP anchor',
    };
  }
  let prev = first;
  let next = last;
  for (const y of years) {
    if (y < year) prev = y;
    if (y > year && next === last) next = y;
  }
  const t = (year - prev) / (next - prev);
  const value = Math.round(anchors[prev] + t * (anchors[next] - anchors[prev]));
  return {
    value,
    coverage: 'interpolated',
    sourceClass: 'cap_derived',
    methodId: `${methodId}_interpolated`,
    sourceNote: `Linear interpolation between CAP ${prev} and ${next}`,
  };
}

function interpolateEiaNonresAtYear(
  eiaRows: Eia861CanonicalElectricityYear[],
  year: number
): BlendedPoint {
  const byYear = new Map(eiaRows.map((r) => [r.year, r.commercial]));
  const direct = byYear.get(year);
  if (direct && direct.status === 'observed_eia861') {
    return eiaPoint(direct, 'burbank_eia861_nonresidential', 'Nonresidential electricity');
  }
  const numericYears = eiaRows.map((r) => r.year).sort((a, b) => a - b);
  let prevY: number | null = null;
  let nextY: number | null = null;
  for (const y of numericYears) {
    if (y < year) prevY = y;
    if (y > year && nextY == null) nextY = y;
  }
  if (prevY != null && nextY != null) {
    const prev = byYear.get(prevY)!;
    const next = byYear.get(nextY)!;
    const t = (year - prevY) / (nextY - prevY);
    const kwh = Math.round(prev.kwh + t * (next.kwh - prev.kwh));
    return {
      value: kwh,
      coverage: 'interpolated',
      sourceClass: 'utility_observed',
      methodId: 'burbank_eia861_nonres_interpolated',
      sourceNote: `EIA-861 linear interpolation between ${prevY} and ${nextY}`,
    };
  }
  const fallback = direct ?? byYear.get(numericYears[0])!;
  return eiaPoint(fallback, 'burbank_eia861_nonresidential', 'Nonresidential electricity');
}

export function blendBurbankResidentialElectricity(
  year: number,
  eiaRow: Eia861CanonicalElectricityYear
): BlendedPoint {
  if ((BURBANK_CAP_PUBLISHED_YEARS as readonly number[]).includes(year)) {
    const cap = capResElec(year);
    if (cap != null) return capPoint(cap, 'burbank_cap_res_elec');
  }
  return eiaPoint(eiaRow.residential, 'burbank_eia861_residential', 'Residential electricity');
}

export function blendBurbankNonresidentialElectricity(
  year: number,
  eiaRow: Eia861CanonicalElectricityYear,
  allEia: Eia861CanonicalElectricityYear[]
): BlendedPoint {
  if ((BURBANK_NONRES_ELEC_CAP_YEARS as readonly number[]).includes(year)) {
    const cap = capComElec(year);
    if (cap != null) {
      return capPoint(cap, 'burbank_cap_commercial_elec');
    }
  }
  if (year === 2015) {
    return interpolateEiaNonresAtYear(allEia, 2015);
  }
  return eiaPoint(eiaRow.commercial, 'burbank_eia861_nonresidential', 'Nonresidential electricity');
}

export function buildCapGasAnchors(): { residential: Record<number, number>; nonresidential: Record<number, number> } {
  const residential: Record<number, number> = {};
  const nonresidential: Record<number, number> = {};
  for (const y of BURBANK_CAP_PUBLISHED_YEARS) {
    const res = capResGas(y);
    const nonres = capNonresGas(y);
    if (res != null) residential[y] = res;
    if (nonres != null) nonresidential[y] = nonres;
  }
  return { residential, nonresidential };
}

/** Residential SoCal filing knots (CAP inventory years take precedence). */
export const BURBANK_SOCAL_GAS_OBSERVED_FILING_YEARS = [2021, 2022, 2023, 2024] as const;

/**
 * Commercial SoCal knots: 2021 omitted (partial filing); 2021 commercial is CAP-interpolated.
 * CAP inventory years still win when present (2022, 2024).
 */
export const BURBANK_SOCAL_COMMERCIAL_GAS_FILING_YEARS = [2022, 2023, 2024] as const;

/** @deprecated Use BURBANK_SOCAL_COMMERCIAL_GAS_FILING_YEARS */
export const BURBANK_SOCAL_COMMERCIAL_GAS_OBSERVED_YEARS = BURBANK_SOCAL_COMMERCIAL_GAS_FILING_YEARS;

export function buildBurbankResResidentialGasObservedAnchors(
  legacyGasRes: Map<number, number>
): Record<number, number> {
  const anchors: Record<number, number> = { ...buildCapGasAnchors().residential };
  for (const y of BURBANK_SOCAL_GAS_OBSERVED_FILING_YEARS) {
    if (anchors[y] != null) continue;
    const v = legacyGasRes.get(y);
    if (v != null && Number.isFinite(v)) anchors[y] = v;
  }
  return anchors;
}

/**
 * Residential natural gas: same harmonization as commercial — CAP + SoCalGas observed knots,
 * linear interpolation between, flat before 2010. No modeled county-ratio series.
 */
export function blendBurbankResidentialGas(
  year: number,
  legacyGasRes: Map<number, number>
): BlendedPoint {
  const anchors = buildBurbankResResidentialGasObservedAnchors(legacyGasRes);
  if (anchors[year] != null) {
    if ((BURBANK_CAP_PUBLISHED_YEARS as readonly number[]).includes(year)) {
      return capPoint(anchors[year], 'burbank_cap_res_gas');
    }
    return {
      value: anchors[year],
      coverage: 'complete_observed',
      sourceClass: 'utility_observed',
      methodId: 'burbank_gas_res_socalgas_observed',
      sourceNote: 'SoCalGas residential (utility filing) — harmonized Analysis series',
    };
  }
  return blendCapOnlyThermsSeries(year, anchors, 'burbank_gas_res_cap_socal_interp');
}


/**
 * Observed commercial gas knots for Analysis: published CAP + SoCalGas filings only (no modeled/backcast).
 */
export function buildBurbankNonresCommercialGasObservedAnchors(
  legacyGasCom: Map<number, number>
): Record<number, number> {
  const anchors: Record<number, number> = { ...buildCapGasAnchors().nonresidential };
  for (const y of BURBANK_SOCAL_COMMERCIAL_GAS_FILING_YEARS) {
    if (anchors[y] != null) continue;
    const v = legacyGasCom.get(y);
    if (v != null && Number.isFinite(v)) anchors[y] = v;
  }
  return anchors;
}

/**
 * Commercial natural gas: linear interpolation between CAP and SoCalGas observed years;
 * flat before first CAP anchor (2010). Modeled county-ratio / NREL series are not used.
 */
export function blendBurbankNonresidentialGas(
  year: number,
  legacyGasCom: Map<number, number>
): BlendedPoint {
  const anchors = buildBurbankNonresCommercialGasObservedAnchors(legacyGasCom);
  if (anchors[year] != null) {
    if ((BURBANK_CAP_PUBLISHED_YEARS as readonly number[]).includes(year)) {
      return capPoint(anchors[year], 'burbank_cap_nonres_gas');
    }
    return {
      value: anchors[year],
      coverage: 'complete_observed',
      sourceClass: 'utility_observed',
      methodId: 'burbank_gas_com_socalgas_observed',
      sourceNote: 'SoCalGas commercial (utility filing) — harmonized Analysis series',
    };
  }
  return blendCapOnlyThermsSeries(year, anchors, 'burbank_gas_com_cap_socal_interp');
}

/**
 * Industrial natural gas: SoCalGas filings from 2022 onward; flat extension of 2022 therms before that.
 * 2021 industrial filing treated as partial — not used.
 */
function buildBurbankIndustrialGasAnchors(): Record<number, number> {
  return { ...BURBANK_SOCAL_INDUSTRIAL_GAS_THERMS_BY_YEAR };
}

export function blendBurbankIndustrialGas(year: number): BlendedPoint {
  const anchors = buildBurbankIndustrialGasAnchors();
  if (anchors[year] != null) {
    return {
      value: anchors[year],
      coverage: 'complete_observed',
      sourceClass: 'utility_observed',
      methodId: 'burbank_gas_ind_socalgas_observed',
      sourceNote:
        'SoCalGas industrial (utility filing) — outside CAP community gas inventory scope',
    };
  }
  return blendCapOnlyThermsSeries(year, anchors, 'burbank_gas_ind_socal');
}
